import { KeyboardEvent, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isSearchableQuery } from 'types/globalSearch';
import { searchResultsPath } from 'utils/globalSearchRoutes';
import { isComposing } from 'utils/isComposing';
import { captureSearchResultsPageOpened, SearchSurface } from 'utils/searchAnalytics';

/**
 * The query the results page is answering, and an empty string anywhere else.
 *
 * What {@link useSharedSearchQuery} puts the fields back to, and what tells the header's box it is
 * carrying a query while nothing is focused.
 */
export function useSearchQueryFromUrl(): string {
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    return pathname === '/search' ? (searchParams.get('q') ?? '') : '';
}

/**
 * The query both search surfaces share, put back by the URL (a shared link, a reload, a step back)
 * and by the sheet opening (the box that was tapped was showing that query). Both are adjusted
 * during render, so neither surface paints a query the other has already left behind.
 *
 * The third value puts the query back the same way, for a surface whose field stays on screen once
 * its list is put away: a search the user left behind must not wait in the box for the next one.
 */
export function useSharedSearchQuery(sheetOpen: boolean): [string, (query: string) => void, () => void] {
    const urlQuery = useSearchQueryFromUrl();
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers
    const [query, setQuery] = useState(urlQuery);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers
    const [mirroredUrlQuery, setMirroredUrlQuery] = useState(urlQuery);
    if (mirroredUrlQuery !== urlQuery) {
        setMirroredUrlQuery(urlQuery);
        setQuery(urlQuery);
    }
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers
    const [wasSheetOpen, setWasSheetOpen] = useState(sheetOpen);
    if (wasSheetOpen !== sheetOpen) {
        setWasSheetOpen(sheetOpen);
        if (sheetOpen) setQuery(urlQuery);
    }

    return [query, setQuery, () => setQuery(urlQuery)];
}

export interface SearchKeyDownOptions {
    /** Exactly what is in the field, undebounced. */
    query: string;
    /** Put the field's panel away. */
    close: () => void;
    /** Which field this is, for the event an unconsumed `Enter` captures. */
    surface?: SearchSurface;
    /**
     * Key navigation for whatever panel is open. Return `true` when the panel consumed the key and
     * the field should only swallow it.
     */
    onArrowDown?: () => boolean;
    onArrowUp?: () => boolean;
    onEnter?: () => boolean;
}

/**
 * The key handling every search field shares — the header's box, the narrow sheet's own and the dialog's.
 *
 * The field keeps the focus wherever it is drawn, so the keys land on it rather than on the list;
 * what the list does with them is the caller's, through the three callbacks. An `Enter` nobody
 * consumed opens the results page for what is typed.
 */
export function useSearchKeyDown({
    query,
    close,
    surface,
    onArrowDown,
    onArrowUp,
    onEnter,
}: SearchKeyDownOptions): (event: KeyboardEvent<HTMLInputElement>) => void {
    const navigate = useNavigate();

    return (event) => {
        switch (event.key) {
            case 'ArrowDown':
                if (onArrowDown?.()) event.preventDefault();
                break;
            case 'ArrowUp':
                if (onArrowUp?.()) event.preventDefault();
                break;
            case 'Enter': {
                // An IME's Enter confirms the word being composed.
                if (isComposing(event)) break;
                event.preventDefault();
                if (onEnter?.()) break;
                const trimmed = query.trim();
                if (!isSearchableQuery(trimmed)) break;
                captureSearchResultsPageOpened(surface);
                close();
                navigate(searchResultsPath(trimmed));
                break;
            }
            case 'Escape':
                event.preventDefault();
                close();
                break;
        }
    };
}
